import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, BadgeCheck, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AddressesSection, DevicesSection, NotificationPrefsSection } from "@/pages/account/ProfileSections";

const TABS = [
  { key: "about", label: "About" },
  { key: "addresses", label: "Addresses" },
  { key: "devices", label: "Devices" },
  { key: "notifications", label: "Notifications" },
];

const QUICK = [
  { to: "/bookings", label: "Bookings" },
  { to: "/messages", label: "Messages" },
  { to: "/favourites", label: "Favourites" },
  { to: "/tickets", label: "Tickets" },
  { to: "/wallet", label: "Wallet" },
  { to: "/plan-day", label: "Plan my day" },
];

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "about";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ full_name: user?.full_name || "", bio: user?.bio || "", location: user?.location || "" });
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const initials = (user.full_name || user.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const save = async () => {
    setSaving(true);
    try {
      const updated = await authApi.updateProfile({
        full_name: form.full_name.trim(),
        bio: form.bio,
        location: form.location,
      });
      setUser(updated);
      setEditing(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-4 flex justify-end"><a href="/settings" className="text-sm font-semibold underline" data-testid="profile-settings-link">Settings, privacy & devices</a></div>
      <div className="overflow-hidden rounded-3xl border border-border bg-card">
        <div className="h-28" style={{ background: "linear-gradient(120deg, var(--fable), var(--provider))" }} />
        <div className="px-6 pb-6">
          <div className="-mt-10 flex items-end justify-between">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-4 border-card text-2xl font-bold text-white" style={{ background: "var(--fable)" }}>
              {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full rounded-xl object-cover" /> : initials}
            </div>
            {!editing && (
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => setEditing(true)} data-testid="profile-edit">
                <Pencil size={14} /> Edit
              </Button>
            )}
          </div>

          {!editing ? (
            <div className="mt-4">
              <h1 className="flex items-center gap-2 font-serif-display text-2xl font-bold" data-testid="profile-name">
                {user.full_name}
                {user.verified && <BadgeCheck size={20} style={{ color: "var(--fable)" }} />}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Mail size={14} /> {user.email}</span>
                {user.location && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {user.location}</span>}
              </div>
              {user.bio && <p className="mt-3 text-[15px] text-foreground/85">{user.bio}</p>}

              <div className="mt-5 flex flex-wrap gap-2">
                {user.is_partner && <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">Provider</span>}
                {user.is_creator && <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">Creator @{user.creator_handle}</span>}
                {user.email_verified && <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">Email verified</span>}
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="pn">Full name</Label>
                <Input id="pn" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="mt-1.5" data-testid="profile-name-input" />
              </div>
              <div>
                <Label htmlFor="pl">Location</Label>
                <Input id="pl" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="mt-1.5" placeholder="e.g. London" data-testid="profile-location-input" />
              </div>
              <div>
                <Label htmlFor="pb">Bio</Label>
                <Textarea id="pb" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} className="mt-1.5" rows={3} data-testid="profile-bio-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving} className="rounded-full" data-testid="profile-save">
                  {saving ? "Saving…" : "Save changes"}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="mt-6 flex gap-2 overflow-x-auto pb-1" aria-label="Profile sections">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setParams(t.key === "about" ? {} : { tab: t.key }, { replace: true })} aria-pressed={tab === t.key} data-testid={`profile-tab-${t.key}`} className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium ${tab === t.key ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>
            {t.label}
          </button>
        ))}
      </nav>

      <section className="mt-5 rounded-3xl border border-border bg-card p-6">
        {tab === "about" && (
          <div data-testid="profile-quick">
            <h2 className="font-bold">Your account</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {QUICK.map((q) => (
                <Link key={q.to} to={q.to} className="rounded-2xl border border-border px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/60">{q.label}</Link>
              ))}
            </div>
          </div>
        )}
        {tab === "addresses" && <AddressesSection />}
        {tab === "devices" && <DevicesSection onSignedOutEverywhere={async () => { await logout(); navigate("/login"); }} />}
        {tab === "notifications" && <NotificationPrefsSection />}
      </section>
    </div>
  );
}
